import reducerRegistry from '@comeon/reducer-registry';
import type { CasinoSharedAction, CasinoSharedThunkAction } from '~/components/casino-shared/ducks/types';
import getGameUrls from '~/components/casino-shared/utilities/getGameUrls';
import { graphyteSearch, graphyteSearchConversion } from '~/components/search/ducks/graphyte';
import {
	CLEAR_KEYWORD,
	SET_GRAPHYTE_CASINO_EXPLORE_RECOMMENDATIONS,
	SET_GRAPHYTE_LIVE_CASINO_EXPLORE_RECOMMENDATIONS,
	SET_GRAPHYTE_REMOTE_RESULTS,
	SET_KEYWORD,
	SET_PAGE,
	SET_SCROLL_POSITION
} from './action-names';
import type { Actions, State } from './types';

const REDUCER_NAME = 'search';

const initialState: State = {
	keyword: new URLSearchParams(window.location.search).get('q') || '',
	page: 1,
	// this will determine whether the first search results render will start a new timer or not
	scrollPosition: 0,
	graphyte: {
		results: [],
		recommendation: [],
		casinoExploreRecommendations: [],
		liveCasinoExploreRecommendations: [],
		latestSearchId: '',
		searchPending: false,
		searchSucceeded: true
	}
};

const reducer = (state = initialState, action: Actions) => {
	switch (action.type) {
		case SET_KEYWORD: {
			return {
				...state,
				page: action.payload.isDesktop ? 4 : 1,
				keyword: action.payload.keyword
			};
		}
		case CLEAR_KEYWORD: {
			return {
				...state,
				page: action.payload.isDesktop ? 4 : 1,
				keyword: '',
				graphyte: {
					...state.graphyte,
					results: [],
					recommendation: []
				}
			};
		}
		case SET_PAGE: {
			return {
				...state,
				page: action.payload
			};
		}
		case SET_SCROLL_POSITION: {
			return {
				...state,
				scrollPosition: action.payload
			};
		}
		case SET_GRAPHYTE_REMOTE_RESULTS: {
			return {
				...state,
				graphyte: {
					...state.graphyte,
					results: action.payload.results,
					recommendation: action.payload.recommendation,
					latestSearchId: action.payload.searchId,
					searchPending: action.payload.pending,
					searchSucceeded: action.payload.succeeded
				}
			};
		}
		case SET_GRAPHYTE_CASINO_EXPLORE_RECOMMENDATIONS: {
			return {
				...state,
				graphyte: {
					...state.graphyte,
					casinoExploreRecommendations: action.payload
				}
			};
		}
		case SET_GRAPHYTE_LIVE_CASINO_EXPLORE_RECOMMENDATIONS: {
			return {
				...state,
				graphyte: {
					...state.graphyte,
					liveCasinoExploreRecommendations: action.payload
				}
			};
		}
		default:
			return state;
	}
};

reducerRegistry.register(REDUCER_NAME, reducer);

const setKeyword = (keyword: string, isDesktop: boolean): CasinoSharedAction => ({
	type: SET_KEYWORD,
	payload: {
		keyword,
		isDesktop
	}
});

const clearKeyword = (isDesktop: boolean): CasinoSharedAction => ({
	type: CLEAR_KEYWORD,
	payload: { isDesktop }
});

const setPage = (page: number): CasinoSharedAction => ({
	type: SET_PAGE,
	payload: page
});

const setScrollPosition = (position: number): CasinoSharedAction => ({
	type: SET_SCROLL_POSITION,
	payload: position
});

const setGraphyteRemoteResults = ({
	results = [],
	recommendation = [],
	searchId = '',
	succeeded,
	pending
}: {
	results?: State['graphyte']['results'];
	recommendation?: State['graphyte']['recommendation'];
	searchId?: string;
	succeeded: boolean;
	pending: boolean;
}): CasinoSharedAction => ({
	type: SET_GRAPHYTE_REMOTE_RESULTS,
	payload: {
		results,
		recommendation,
		searchId,
		succeeded,
		pending
	}
});

const remoteSearch =
	(query: string): CasinoSharedThunkAction =>
	async (dispatch, getState, { imageDescriptors, imageFormats, license }) => {
		dispatch(
			setGraphyteRemoteResults({
				pending: true,
				succeeded: true
			})
		);

		const result = await graphyteSearch(query, getState, imageDescriptors, imageFormats, license);

		if (result.results && result.results.length) {
			result.results = result.results.map(r => {
				const { realModeUrl, funModeUrl } = getGameUrls(r, license);

				return { ...r, realModeUrl, funModeUrl };
			});
		}

		if (result.recommendation) {
			result.recommendation = result.recommendation.map(r => {
				const { realModeUrl, funModeUrl } = getGameUrls(r, license);

				return { ...r, realModeUrl, funModeUrl };
			});
		}

		dispatch(setGraphyteRemoteResults(result));
	};

const searchConversion = (gameCode: string): CasinoSharedThunkAction => {
	return (_dispatch, getState) => {
		const {
			brand: { graphyteSearchEnabled },
			search: {
				graphyte: { searchSucceeded }
			}
		} = getState();

		if (graphyteSearchEnabled && searchSucceeded) {
			const {
				search: {
					keyword,
					graphyte: { latestSearchId }
				}
			} = getState();
			graphyteSearchConversion(gameCode, latestSearchId, keyword);
		}
	};
};

export { clearKeyword, remoteSearch, searchConversion, setKeyword, setPage, setScrollPosition };
